/**
 * useSharedReminderSync — Layer 1: Client-side cleanup for shared realm reminders.
 *
 * Reminders for shared tasks are CREATED by:
 * - Layer 2 (DO-to-DO fan-out via Dexie Cloud sync)
 * - User manually adding reminders to shared tasks (normal flow)
 *
 * The existing `useReminderSync` hook handles scheduling local notifications
 * for ALL reminders (including shared ones). This hook handles CLEANUP only:
 * - Task completed/deleted → soft-delete corresponding reminder
 * - User opts out (enableReminders → 0) → soft-delete shared reminders
 *
 * Must be mounted in AppLayout (Tier 2) — runs only when app is in foreground.
 */

import { useEffect, useRef } from "react"
import { useLiveQueryImmediate } from "@/db/hooks"
import { db } from "@/db/schema"
import { ObjectStatus } from "@totodo/operations"
import { getNow } from "@totodo/operations"

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Debounce to coalesce rapid changes. */
const DEBOUNCE_MS = 300

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useSharedReminderSync(): void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const runningRef = useRef(false)

  // 1. Get container IDs with enableReminders=1
  const [reminderContainerIds] = useLiveQueryImmediate(async () => {
    const prefs = await db.sharedViewPref
      .filter((p) => p.enableReminders === 1 && p.scope === "container")
      .toArray()
    return prefs.map((p) => p.id.slice(1))
  }, ["reminderSync-containers"], [])

  // 2. Get per-task IDs with enableReminders=1
  const [reminderTaskIds] = useLiveQueryImmediate(async () => {
    const prefs = await db.sharedViewPref
      .filter((p) => p.enableReminders === 1 && p.scope === "task")
      .toArray()
    return prefs.map((p) => p.id.slice(1))
  }, ["reminderSync-tasks"], [])

  // 3. Watch active reminders for shared tasks (those with "#" prefixed startAtIndex entries
  //    or in shared containers). We identify "shared reminders" by checking if the task
  //    belongs to a shared realm.
  const [sharedReminders] = useLiveQueryImmediate(async () => {
    // One getAll() and a JS filter. Dexie's .filter() would walk a cursor with
    // a main-thread round trip per reminder, holding the read open for seconds
    // at startup (and blocking reminder writes meanwhile).
    const reminders = (await db.reminder.toArray()).filter(
      (r) => !r.isDeleted && !!r.realmId && r.realmId.startsWith("rlm~"),
    )
    return reminders
  }, ["reminderSync-shared"], [])

  // 4. Cleanup effect
  useEffect(() => {
    if (!sharedReminders?.length) return

    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      if (runningRef.current) return
      runningRef.current = true
      cleanupSharedReminders(
        sharedReminders,
        reminderContainerIds ?? [],
        reminderTaskIds ?? [],
      )
        .catch((err) =>
          console.warn("[shared-reminder-sync] cleanup error:", err),
        )
        .finally(() => {
          runningRef.current = false
        })
    }, DEBOUNCE_MS)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [sharedReminders, reminderContainerIds, reminderTaskIds])
}

// ---------------------------------------------------------------------------
// Cleanup logic
// ---------------------------------------------------------------------------

async function cleanupSharedReminders(
  reminders: { id: string; taskId: string; realmId?: string }[],
  containerIds: string[],
  taskIds: string[],
): Promise<void> {
  const toSoftDelete: string[] = []

  for (const reminder of reminders) {
    const task = await db.totodo.get(reminder.taskId)

    if (!task) {
      // Task no longer exists → soft-delete reminder
      toSoftDelete.push(reminder.id)
      continue
    }

    // Task completed or deleted → soft-delete reminder
    if (task.isDeleted || task.status !== ObjectStatus.Unfinished) {
      toSoftDelete.push(reminder.id)
      continue
    }

    // Check if this task is still covered by an active enableReminders pref
    // Use realmId (not parentPath) — sections have parentPath="root/sect-xxx"
    // but the pref is keyed by container (project) ID from realmId.
    const containerId = task.realmId?.replace("rlm~", "") ?? ""
    const isFollowedViaContainer = containerIds.includes(containerId)
    const isFollowedViaTask = taskIds.includes(reminder.taskId)

    if (!isFollowedViaContainer && !isFollowedViaTask) {
      // User opted out → soft-delete
      toSoftDelete.push(reminder.id)
    }
  }

  if (toSoftDelete.length) {
    const now = getNow()
    await db.transaction("rw", db.reminder, async () => {
      for (const id of toSoftDelete) {
        await db.reminder.update(id, { isDeleted: 1, deletedAt: now })
      }
    })
  }
}
